import React from "react";
import { Badge, HashCell } from "@/components/primitives";
import { VoteChip } from "@/components/governance/GovShell";
import { govActionLabel } from "@/lib/governance";
import { ipfsToHttp } from "@/lib/format";

// External anchor (CIP-100 rationale / metadata) link. Decodes ipfs:// → gateway.
export const AnchorLink = ({ url }) => {
  const href = ipfsToHttp(url);
  if (!href) return <span className="text-muted-foreground">—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="gov-anchor-link"
      className="text-primary hover:underline underline-offset-2"
    >
      doc ↗
    </a>
  );
};

const roleTone = (role) => (role === "drep" ? "info" : role === "spo" ? "warn" : "default");

// Columns for a proposal's own votes feed (voter-centric).
export const proposalVoteColumns = () => [
  {
    key: "voter",
    label: "Voter",
    render: (r) => (
      <span className="inline-flex items-center gap-2 flex-wrap">
        <Badge tone={roleTone(r.voter_role)}>{r.voter_role}</Badge>
        <HashCell value={r.voter_cc_cold || r.voter} len={8} />
      </span>
    ),
  },
  { key: "vote", label: "Vote", render: (r) => <VoteChip vote={r.vote} /> },
  { key: "superseded", label: "State", render: (r) => (r.superseded ? <Badge>superseded</Badge> : <Badge tone="success">counted</Badge>) },
  { key: "epoch", label: "Epoch", align: "right", render: (r) => <span className="text-muted-foreground">{r.epoch ?? "—"}</span> },
  { key: "rationale", label: "Rationale", align: "right", render: (r) => <AnchorLink url={r.anchor_url} /> },
  { key: "tx", label: "Vote Tx", align: "right", render: (r) => <HashCell value={r.tx_hash} len={8} /> },
];

// Columns for an entity's votes feed (DRep / committee member / pool) —
// proposal-centric: each row links back to the governance action voted on.
export const entityVoteColumns = (navigate) => [
  {
    key: "proposal",
    label: "Governance Action",
    render: (r) => (
      <button
        type="button"
        data-testid={`entity-vote-${r.proposal_tx_hash}-${r.proposal_cert_index}`}
        onClick={() => navigate(`/governance/proposals/${r.proposal_tx_hash}/${r.proposal_cert_index}`)}
        className="text-left text-foreground hover:text-primary underline decoration-dotted decoration-border underline-offset-2 transition-colors"
        title={r.proposal_id}
      >
        {govActionLabel(r.proposal_type)}
      </button>
    ),
  },
  { key: "vote", label: "Vote", render: (r) => <VoteChip vote={r.vote} /> },
  { key: "superseded", label: "State", render: (r) => (r.superseded ? <Badge>superseded</Badge> : <Badge tone="success">counted</Badge>) },
  { key: "epoch", label: "Epoch", align: "right", render: (r) => <span className="text-muted-foreground">{r.epoch ?? "—"}</span> },
  { key: "rationale", label: "Rationale", align: "right", render: (r) => <AnchorLink url={r.anchor_url} /> },
  { key: "tx", label: "Vote Tx", align: "right", render: (r) => <HashCell value={r.tx_hash} len={8} /> },
];
